import React from "react";
import SectionBlock from "../../../../global/components/FeatureCard/SectionBlock";
import smartSurveillanceRecording from "../../../../assets/images/videoRecording/smart-surveillance-recording.png";
import advancedVideoControl from "../../../../assets/images/videoRecording/advanced-video-control.png";
import intelligentPlayback from "../../../../assets/images/videoRecording/intelligent-playback.png";
import eventBasedTagging from "../../../../assets/images/videoRecording/event-based-tagging.png";
import actionConfiguration from "../../../../assets/images/videoRecording/action-configuration.png";
import taggedVideosReports from "../../../../assets/images/videoRecording/tagged-videos-reports.png";

const PAGE_X = "px-6 lg:px-[120px]";

const sections = [
  {
    image: smartSurveillanceRecording,
    label: "Smart Surveillance Recording",
    description:
      'Our system captures and stores video footage of access-related activities through integrated NVR and camera channels. Users can replay recorded content by date, hour, and minute, streamlining incident investigations. Smart triggers based on <em>Event Actions</em>—like door access or system alerts—initiate recording automatically. In parallel, <em>Schedule Actions</em> allow pre-defined time-based recording. This dual mechanism ensures consistent coverage while optimizing video storage.',
    imageLeft: true,
    imageBg: "#f8f8f8",
    textBg: "#f8f8f8",
    textAlign: "left",
    bodyColor: "#3b3b3b",
    imageMaxWidth: "540px",
    sectionHeight: "540px",
    htmlDescription: true,
  },
  {
    image: advancedVideoControl,
    label: "Advanced Video Control and Scheduling",
    description:
      'Gain full control of surveillance recording with flexible <em>Action Modes</em>—including Record On, Off, and Instant Record—for manual command over video capture. Combine this with <em>Event-Based Triggers</em> (e.g., access attempts, alarms) and <em>Schedule-Based Actions</em> (e.g., working hours) to create tailored recording rules. The system enables seamless switching between reactive and proactive recording, ensuring no event goes unnoticed while maximizing operational efficiency.',
    imageLeft: false,
    imageBg: "#ffffff",
    textBg: "#ffffff",
    textAlign: "right",
    bodyColor: "#3b3b3b",
    imageMaxWidth: "540px",
    sectionHeight: "540px",
    htmlDescription: true,
  },
  {
    image: intelligentPlayback,
    label: "Visual Monitoring with Intelligent Playback",
    description:
      'Experience intuitive video playback tied directly to recorded events and scheduled periods. With filter options by timestamp, users can easily review and analyze past surveillance footage. <em>Event Action-based recording</em> ensures real-time response to unexpected activities, while <em>Schedule Actions</em> maintain routine monitoring. Dashboards and reports provide centralized access, turning video data into actionable insights for improved security oversight and compliance.',
    imageLeft: true,
    imageBg: "#f8f8f8",
    textBg: "#f8f8f8",
    textAlign: "left",
    bodyColor: "#3b3b3b",
    imageMaxWidth: "540px",
    sectionHeight: "540px",
    htmlDescription: true,
  },
  {
    image: eventBasedTagging,
    label: "Automated Event-Based Video Tagging",
    description:
      "Our platform enables automated video tagging through the Event Action feature. When users assign the action type as Tagging, the system links surveillance footage to access events like unauthorized entry or alarms. A short clip is recorded and tagged precisely to the event timestamp, accessible instantly from dashboards. This eliminates manual searches and ensures critical moments are always captured.",
    imageLeft: false,
    imageBg: "#ffffff",
    textBg: "#ffffff",
    textAlign: "right",
    bodyColor: "#3b3b3b",
    imageMaxWidth: "540px",
    sectionHeight: "540px",
  },
  {
    image: actionConfiguration,
    label: "Intelligent Control via Action Configuration",
    description:
      "Users can configure Action Controls to define which events trigger video tagging. Whether it's a door held open or a forced entry, the system captures a short video clip at the event time. Tagged videos appear beside event names in dashboards and reports with a video icon, simplifying tracking and enhancing real-time forensic analysis.",
    imageLeft: true,
    imageBg: "#f8f8f8",
    textBg: "#f8f8f8",
    textAlign: "left",
    bodyColor: "#3b3b3b",
    imageMaxWidth: "540px",
    sectionHeight: "540px",
  },
  {
    image: taggedVideosReports,
    label: "Intelligent Control via Action Configuration",
    description:
      "Tagged videos are embedded into the log reports, offering fast, context-rich playback. From Access Logs to Smart Reports, users can click a video icon next to any event to view a short, relevant clip. This improves incident verification, speeds up audits, and boosts decision-making efficiency in high-security scenarios.",
    imageLeft: false,
    imageBg: "#ffffff",
    textBg: "#ffffff",
    textAlign: "right",
    bodyColor: "#3b3b3b",
    imageMaxWidth: "540px",
    sectionHeight: "540px",
  },
];

export const VideoRecordingContent = () => (
  <div className="bg-white w-full">
    <section
      className={`bg-white flex flex-col items-center justify-center gap-6 ${PAGE_X} pt-28 sm:pt-24 min-h-[258px] text-center`}
    >
      <h1 className="text-black text-[32px] font-medium font-['Inter'] leading-normal max-w-[1004px]">
        Intelligent Video Recording & Event Tagging
      </h1>
      <p className="text-[#5b5b5b] text-[20px] font-medium font-['Poppins'] leading-normal max-w-[872px]">
        Automate surveillance recording, event-based video tagging, and intelligent
        playback for faster incident response and enhanced security visibility.
      </p>
    </section>

    <div className="w-full bg-white mt-6">
      {sections.map((section, index) => (
        <SectionBlock
          key={`${section.label}-${index}`}
          {...section}
        />
      ))}
    </div>
  </div>
);

export default VideoRecordingContent;
